import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  Modal,
  Platform,
  ActivityIndicator,
  Linking,
  Keyboard,
  useWindowDimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import RenderHtml from 'react-native-render-html';
import { WebView } from 'react-native-webview';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
// Syllabus break video upload preserved in src/utils/syllabusBreakDisplayUrlUpload.js
// APIs: PUT /api/breaks/:id, POST /api/youtube/upload, POST /api/youtube/upload-binary

import { getSyllabusGradeIconSource } from '../utils/syllabusGradeIcons';
const SYLLABUS_OMIT_GRADE_NAMES = new Set(['Beginner']);

const SYLLABUS_TECHNIQUE_FONT = Platform.select({
  web: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  default: undefined,
});

const SYLLABUS_TECHNIQUE_TEXT = {
  fontSize: 15,
  lineHeight: 22,
  ...(SYLLABUS_TECHNIQUE_FONT ? { fontFamily: SYLLABUS_TECHNIQUE_FONT } : {}),
};

const SYLLABUS_TABLE_MIN_WIDTH = 860;
const SYLLABUS_TABLE_COLUMNS = [
  { key: 'break_name', label: 'Break', flex: 1.22 },
  { key: 'side', label: 'Side', flex: 0.42 },
  { key: 'weapon', label: 'Weapon', flex: 0.72 },
  { key: 'material_type', label: 'Material', flex: 0.78 },
  { key: 'quantity', label: 'Qty', flex: 0.38 },
  { key: 'size_inches', label: 'Size (in)', flex: 0.48 },
  { key: 'total_size_inches', label: 'Total (in)', flex: 0.52 },
  { key: 'height', label: 'Height', flex: 0.72 },
];

const SYLLABUS_VIDEO_COL_FLEX = 0.36;
const SYLLABUS_VIDEO_CAMERA_COLOR = '#2e7d32';

function formatSyllabusField(b, key) {
  const v = b?.[key];
  if (v == null || v === '') return '—';
  if (key === 'height' && String(v).trim().toLowerCase() === 'n/a') return '—';
  if (key === 'size_inches') {
    const n = Number(v);
    if (Number.isFinite(n)) return String(Math.round(n));
  }
  if (key === 'side' && String(v).trim().toLowerCase() === 'good side') {
    return 'Good';
  }
  if (key === 'side' && String(v).trim().toLowerCase() === 'offside') {
    return 'Offside';
  }
  return String(v);
}

const SYLLABUS_FOOT_PARTS = new Set(['foot', 'knee', 'toes', 'shins']);

function syllabusBodyGroup(partOfBody) {
  return SYLLABUS_FOOT_PARTS.has(String(partOfBody || '').trim().toLowerCase())
    ? 1
    : 0;
}

function sortSyllabusBreaks(breaks) {
  if (!Array.isArray(breaks)) return [];
  return [...breaks].sort((a, b) => {
    const byBody = syllabusBodyGroup(a.part_of_body) - syllabusBodyGroup(b.part_of_body);
    if (byBody !== 0) return byBody;
    return String(a.break_name || '').localeCompare(String(b.break_name || ''), undefined, {
      sensitivity: 'base',
    });
  });
}

function getYouTubeEmbedUrl(url) {
  if (!url) return '';
  const u = String(url);
  const shorts = u.match(/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shorts) return `https://www.youtube.com/embed/${shorts[1]}`;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = u.match(regExp);
  const videoId = match && match[2] && match[2].length === 11 ? match[2] : null;
  return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
}

function isYouTubeUrl(url) {
  if (!url) return false;
  return /youtube\.com|youtu\.be/.test(String(url));
}

function hasDisplayUrl(b) {
  if (!b || b.display_url == null) return false;
  const s = String(b.display_url).trim();
  return s.length > 0 && s.toLowerCase() !== 'null';
}

function sanitizeRichHtml(html) {
  if (html == null || typeof html !== 'string') return '';
  return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
}

function isRichHtmlDescription(s) {
  return /<\/?[a-z][a-z0-9]*\b/i.test(String(s));
}

function descriptionToPlainLines(description) {
  if (description == null) return [];
  return String(description)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

function isDescriptionEmpty(description) {
  const raw = description == null ? '' : String(description);
  const stripped = raw
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim();
  return stripped.length === 0;
}

function SyllabusTechniqueDescription({ description, theme, styles, contentWidth }) {
  const raw = description == null ? '' : String(description);
  const safe = sanitizeRichHtml(raw);
  const wide = Math.max(160, contentWidth || 280);

  if (isDescriptionEmpty(safe)) {
    return (
      <Text
        style={[
          styles.techniqueEmptyText,
          SYLLABUS_TECHNIQUE_TEXT,
          { color: theme.colors.text },
        ]}
      >
        No techniques content for this grade yet.
      </Text>
    );
  }

  if (!isRichHtmlDescription(safe)) {
    const lines = descriptionToPlainLines(safe);
    return lines.map((line, idx) => (
      <View key={`T${idx}`} style={styles.techniqueBulletLine}>
        <Text
          style={[
            styles.techniqueBulletChar,
            SYLLABUS_TECHNIQUE_TEXT,
            { color: theme.colors.text },
          ]}
        >
          •
        </Text>
        <Text
          style={[
            styles.techniqueBodyText,
            SYLLABUS_TECHNIQUE_TEXT,
            { color: theme.colors.text },
          ]}
        >
          {line}
        </Text>
      </View>
    ));
  }

  const htmlTagText = { ...SYLLABUS_TECHNIQUE_TEXT };

  return (
    <RenderHtml
      contentWidth={wide}
      source={{ html: safe }}
      baseStyle={{
        color: theme.colors.text,
        ...SYLLABUS_TECHNIQUE_TEXT,
      }}
      tagsStyles={{
        body: { margin: 0, ...htmlTagText },
        p: { marginTop: 0, marginBottom: 8, ...htmlTagText },
        ul: { marginBottom: 8, paddingLeft: 18, ...htmlTagText },
        ol: { marginBottom: 8, paddingLeft: 18, ...htmlTagText },
        li: { marginBottom: 4, ...htmlTagText },
        h1: { fontSize: 20, marginBottom: 8, fontWeight: '700', ...htmlTagText },
        h2: { fontSize: 18, marginBottom: 6, fontWeight: '700', ...htmlTagText },
        h3: { fontSize: 16, marginBottom: 6, fontWeight: '600', ...htmlTagText },
        strong: { fontWeight: '700', ...htmlTagText },
        b: { fontWeight: '700', ...htmlTagText },
        a: {
          color: theme.colors.primary || '#d4af37',
          textDecorationLine: 'underline',
          ...htmlTagText,
        },
      }}
    />
  );
}

function SyllabusViewToggle({ value, onChange, styles, disabled }) {
  const isBreaks = value === 'breaks';
  return (
    <View style={styles.syllabusViewToggleWrap}>
      <View
        style={styles.segmentedToggle}
        accessibilityRole="tablist"
        accessibilityLabel="Syllabus content type"
        {...(Platform.OS === 'web' ? { dataSet: { syllabusViewToggle: 'true' } } : {})}
      >
        <TouchableOpacity
          style={[
            styles.segmentedToggleOption,
            isBreaks && styles.segmentedToggleOptionActiveOff,
          ]}
          onPress={() => onChange('breaks')}
          disabled={disabled}
          accessibilityRole="tab"
          accessibilityState={{ selected: isBreaks }}
          accessibilityLabel="Breaks"
          activeOpacity={0.85}
          {...(Platform.OS === 'web'
            ? { dataSet: { syllabusViewToggleOption: 'breaks', syllabusViewToggleActive: isBreaks ? 'true' : 'false' } }
            : {})}
        >
          <Text
            style={[styles.segmentedToggleText, isBreaks && styles.segmentedToggleTextActive]}
          >
            Breaks
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[
            styles.segmentedToggleOption,
            !isBreaks && styles.segmentedToggleOptionActiveOn,
          ]}
          onPress={() => onChange('technique')}
          disabled={disabled}
          accessibilityRole="tab"
          accessibilityState={{ selected: !isBreaks }}
          accessibilityLabel="Technique"
          activeOpacity={0.85}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  syllabusViewToggleOption: 'technique',
                  syllabusViewToggleActive: !isBreaks ? 'true' : 'false',
                },
              }
            : {})}
        >
          <Text
            style={[styles.segmentedToggleText, !isBreaks && styles.segmentedToggleTextActive]}
          >
            Technique
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function SyllabusTechniqueColumnHeaderRow({ styles, isDarkMode }) {
  const headerRowBg = isDarkMode
    ? styles.syllabusHeaderRowBgDark
    : styles.syllabusHeaderRowBgLight;
  const headerTextStyle = isDarkMode
    ? styles.syllabusHeaderTextDark
    : styles.syllabusHeaderTextLight;
  return (
    <View
      style={[styles.syllabusTechniqueHeaderRow, headerRowBg]}
      {...(Platform.OS === 'web' ? { dataSet: { syllabusTechniqueHeaderRow: 'true' } } : {})}
    >
      <Text style={[styles.syllabusTechniqueHeaderCell, headerTextStyle]}>Techniques</Text>
    </View>
  );
}

function SyllabusTechniqueGradeSection({ section, styles, theme, contentWidth }) {
  const isDarkMode = theme.isDarkMode;
  const rowStyle = styles.tableRowBase;
  return (
    <View
      style={styles.section}
      {...(Platform.OS === 'web' ? { dataSet: { syllabusTechniqueSection: 'true' } } : {})}
    >
      <View
        style={styles.gradeHeaderBlock}
        {...(Platform.OS === 'web' ? { dataSet: { syllabusGradeIconBlock: 'true' } } : {})}
      >
        <Image
          source={getSyllabusGradeIconSource(section.grade_name)}
          style={styles.gradeIconLarge}
          contentFit="contain"
          transition={0}
        />
        <Text style={styles.gradeTitleCentered}>{section.grade_name}</Text>
      </View>
      <View style={styles.syllabusTechniqueTableCard}>
        <SyllabusTechniqueColumnHeaderRow styles={styles} isDarkMode={isDarkMode} />
        <View
          style={[styles.syllabusTechniqueDataRow, rowStyle]}
          {...(Platform.OS === 'web' ? { dataSet: { syllabusTechniqueRow: 'true' } } : {})}
        >
          <View
            style={styles.techniqueDescriptionInner}
            {...(Platform.OS === 'web'
              ? { dataSet: { syllabusTechniqueDescriptionInner: 'true' } }
              : {})}
          >
            <SyllabusTechniqueDescription
              description={section.description}
              theme={theme}
              styles={styles}
              contentWidth={contentWidth}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

function SyllabusColumnHeaderRow({ styles, isDarkMode, tableInnerStyle }) {
  const headerRowBg = isDarkMode
    ? styles.syllabusHeaderRowBgDark
    : styles.syllabusHeaderRowBgLight;
  const headerTextStyle = isDarkMode
    ? styles.syllabusHeaderTextDark
    : styles.syllabusHeaderTextLight;
  const headerIconColor = isDarkMode ? '#FDFD0A' : '#8b6914';
  return (
    <View
      style={[styles.syllabusHeaderRow, tableInnerStyle, headerRowBg]}
      {...(Platform.OS === 'web'
        ? { dataSet: { syllabusHeaderRow: 'true' } }
        : {})}
    >
      {SYLLABUS_TABLE_COLUMNS.map((col) => (
        <Text
          key={col.key}
          style={[styles.syllabusHeaderCell, { flex: col.flex }, headerTextStyle]}
          numberOfLines={2}
        >
          {col.label}
        </Text>
      ))}
      <View style={[styles.syllabusVideoCol, { flex: SYLLABUS_VIDEO_COL_FLEX }]}>
        <MaterialIcons
          name="videocam"
          size={17}
          color={headerIconColor}
          style={styles.syllabusVideoHeaderIcon}
          accessibilityLabel="Video"
        />
      </View>
    </View>
  );
}

function SyllabusBreakRow({ breakItem, alt, onPressRow, styles, isDarkMode, tableInnerStyle }) {
  const withUrl = hasDisplayUrl(breakItem);
  const textStyle = isDarkMode ? styles.breakTextNoUrlDark : styles.breakTextNoUrlLight;
  const rowStyle = alt ? styles.tableRowAlt : styles.tableRowBase;
  return (
    <Pressable
      style={({ pressed }) => [
        styles.syllabusDataRow,
        tableInnerStyle,
        rowStyle,
        pressed && withUrl && styles.tableRowPressed,
      ]}
      onPress={() => (withUrl ? onPressRow(breakItem) : undefined)}
      disabled={!withUrl}
      {...(Platform.OS === 'web'
        ? { dataSet: { syllabusBreakRow: alt ? 'alt' : 'base' } }
        : {})}
    >
      {SYLLABUS_TABLE_COLUMNS.map((col) => (
        <Text
          key={col.key}
          style={[styles.syllabusCell, { flex: col.flex }, styles.breakRowText, textStyle]}
          numberOfLines={4}
        >
          {formatSyllabusField(breakItem, col.key)}
        </Text>
      ))}
      <View style={[styles.syllabusVideoCol, { flex: SYLLABUS_VIDEO_COL_FLEX }]} pointerEvents="none">
        {withUrl ? (
          <MaterialIcons
            name="videocam"
            size={26}
            color={SYLLABUS_VIDEO_CAMERA_COLOR}
            accessibilityLabel="Has video"
          />
        ) : null}
      </View>
    </Pressable>
  );
}

function SyllabusGradeSection({
  section,
  styles,
  isDarkMode,
  windowWidth,
  onPressRow,
}) {
  const horizontalScrollRef = useRef(null);
  const [hScrollX, setHScrollX] = useState(0);
  const [hContentW, setHContentW] = useState(0);
  const [hViewportW, setHViewportW] = useState(0);
  const [hTrackW, setHTrackW] = useState(0);

  const tableScrollContentWidth = Math.max(
    SYLLABUS_TABLE_MIN_WIDTH,
    windowWidth - 48
  );
  const syllabusTableInnerStyle = {
    width: tableScrollContentWidth,
    alignSelf: 'flex-start',
  };

  const maxHScroll =
    hViewportW > 0 ? Math.max(0, hContentW - hViewportW) : 0;

  const scrollHorizontalBy = useCallback(
    (dir) => {
      if (maxHScroll <= 0) return;
      const step = Math.max(80, Math.round(hViewportW * 0.35));
      const next = Math.max(
        0,
        Math.min(maxHScroll, hScrollX + (dir > 0 ? step : -step))
      );
      horizontalScrollRef.current?.scrollTo?.({ x: next, animated: true });
    },
    [maxHScroll, hScrollX, hViewportW]
  );

  const handleHorizontalTrackPress = useCallback(
    (e) => {
      const x = e.nativeEvent.locationX;
      if (maxHScroll <= 0 || hTrackW <= 0) return;
      const ratio = Math.max(0, Math.min(1, x / hTrackW));
      horizontalScrollRef.current?.scrollTo?.({
        x: ratio * maxHScroll,
        animated: true,
      });
    },
    [maxHScroll, hTrackW]
  );

  const thumbW =
    maxHScroll > 0 && hTrackW > 0 && hContentW > 0
      ? Math.max(40, Math.min(hTrackW, (hViewportW / hContentW) * hTrackW))
      : 48;
  const thumbLeft =
    maxHScroll > 0 && hTrackW > 0 && thumbW > 0
      ? (hScrollX / maxHScroll) * Math.max(0, hTrackW - thumbW)
      : 0;

  return (
    <View style={styles.section}>
      <View
        style={styles.gradeHeaderBlock}
        {...(Platform.OS === 'web'
          ? { dataSet: { syllabusGradeIconBlock: 'true' } }
          : {})}
      >
        <Image
          source={getSyllabusGradeIconSource(section.grade_name)}
          style={styles.gradeIconLarge}
          contentFit="contain"
          transition={0}
        />
        <Text style={styles.gradeTitleCentered}>{section.grade_name}</Text>
      </View>
      <ScrollView
        ref={horizontalScrollRef}
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator
        scrollEventThrottle={16}
        onScroll={(ev) => setHScrollX(ev.nativeEvent.contentOffset.x)}
        onContentSizeChange={(w) => setHContentW(w)}
        onLayout={(ev) => setHViewportW(ev.nativeEvent.layout.width)}
        style={styles.syllabusHorizontalScroll}
        contentContainerStyle={styles.syllabusHorizontalScrollContent}
        {...(Platform.OS === 'web'
          ? {
              // @ts-ignore — RN web — second class overrides global ScrollView overflow-x:hidden
              className:
                'records-list-horizontal-scroll syllabus-table-horizontal-scroll',
            }
          : {})}
      >
        <View style={styles.syllabusTableCard}>
          <SyllabusColumnHeaderRow
            styles={styles}
            isDarkMode={isDarkMode}
            tableInnerStyle={syllabusTableInnerStyle}
          />
          {section.breaks.length === 0 ? (
            <Text style={styles.emptySection}>No breaks for this grade.</Text>
          ) : (
            section.breaks.map((b, ri) => (
              <SyllabusBreakRow
                key={b.id}
                breakItem={b}
                alt={ri % 2 === 0}
                onPressRow={onPressRow}
                styles={styles}
                isDarkMode={isDarkMode}
                tableInnerStyle={syllabusTableInnerStyle}
              />
            ))
          )}
        </View>
      </ScrollView>
      {maxHScroll > 0 && (
        <View
          style={styles.hScrollBar}
          {...(Platform.OS === 'web'
            ? { dataSet: { syllabusScrollbar: 'true' } }
            : {})}
        >
          <TouchableOpacity
            style={styles.hScrollArrow}
            onPress={() => scrollHorizontalBy(-1)}
            accessibilityLabel="Scroll table left"
          >
            <Text style={styles.hScrollArrowText}>◀</Text>
          </TouchableOpacity>
          <Pressable
            style={styles.hScrollTrack}
            onLayout={(ev) => setHTrackW(ev.nativeEvent.layout.width)}
            onPress={handleHorizontalTrackPress}
          >
            <View
              pointerEvents="none"
              style={[
                styles.hScrollThumb,
                {
                  width: thumbW,
                  left: thumbLeft,
                },
              ]}
            />
          </Pressable>
          <TouchableOpacity
            style={styles.hScrollArrow}
            onPress={() => scrollHorizontalBy(1)}
            accessibilityLabel="Scroll table right"
          >
            <Text style={styles.hScrollArrowText}>▶</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

export default function SyllabusPage({ navigation, route }) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const stopwatchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [grades, setGrades] = useState([]);
  const [gradeDropdownOpen, setGradeDropdownOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState('all');
  const [syllabusView, setSyllabusView] = useState('breaks');
  const [sections, setSections] = useState([]);
  const [techniqueSections, setTechniqueSections] = useState([]);
  const [loading, setLoading] = useState(false);

  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [videoBreak, setVideoBreak] = useState(null);

  useEffect(() => {
    const uid = route?.params?.userId ?? route?.params?.user?.id;
    if (route?.params?.user) setCurrentUser(route.params.user);
    else if (uid) {
      fetch(`${getApiUrl()}/api/users/${uid}`)
        .then((r) => r.json())
        .then((d) => {
          if (d?.id) setCurrentUser(d);
        })
        .catch(() => {});
    }
  }, [route?.params?.user, route?.params?.userId]);

  const fetchGrades = useCallback(async () => {
    try {
      const res = await fetch(`${getApiUrl()}/api/grades`);
      if (res.ok) {
        const data = await res.json();
        setGrades(
          Array.isArray(data)
            ? data.filter((g) => g?.grade_name && !SYLLABUS_OMIT_GRADE_NAMES.has(g.grade_name))
            : []
        );
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchGrades();
  }, [fetchGrades]);

  const fetchBreaksForGrade = async (gradeName) => {
    const res = await fetch(
      `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(gradeName)}`
    );
    if (!res.ok) return [];
    const data = await res.json();
    return sortSyllabusBreaks(data);
  };

  const fetchAllGradeTechniques = async () => {
    const res = await fetch(`${getApiUrl()}/api/grade-techniques`);
    if (!res.ok) {
      console.error('grade-techniques fetch failed:', res.status);
      return [];
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  };

  const loadSections = async (key) => {
    setLoading(true);
    setSections([]);
    try {
      if (key === 'all') {
        const list = await Promise.all(
          grades.map(async (g) => ({
            grade_name: g.grade_name,
            breaks: await fetchBreaksForGrade(g.grade_name),
          }))
        );
        setSections(list);
      } else if (key) {
        const breaks = await fetchBreaksForGrade(key);
        setSections([{ grade_name: key, breaks }]);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadTechniqueSections = async (key) => {
    setLoading(true);
    setTechniqueSections([]);
    try {
      const rows = await fetchAllGradeTechniques();
      const byGradeId = new Map(
        rows.map((row) => [Number(row.grade_id), row])
      );
      let gradeList = [...grades];
      if (key && key !== 'all') {
        gradeList = gradeList.filter((g) => g.grade_name === key);
      }
      const list = gradeList.map((g) => {
        const rec = byGradeId.get(Number(g.id));
        return {
          grade_name: g.grade_name,
          grade_id: g.id,
          grade_rank_order: g.grade_rank_order,
          description: rec?.description ?? '',
        };
      });
      setTechniqueSections(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedKey && grades.length) {
      if (syllabusView === 'breaks') {
        void loadSections(selectedKey);
      } else {
        void loadTechniqueSections(selectedKey);
      }
    }
  }, [selectedKey, grades, syllabusView]);

  const openVideoModal = (b) => {
    setVideoBreak(b);
    setVideoModalVisible(true);
  };

  const handleBreakRowPress = (b) => {
    if (hasDisplayUrl(b)) {
      openVideoModal(b);
    }
  };

  const styles = getStyles(theme);

  const techniqueContentWidth = Math.max(280, windowWidth - 88);

  const syllabusScrollTableChildren =
    !loading && syllabusView === 'breaks'
      ? sections.map((section, si) => (
          <SyllabusGradeSection
            key={`${section.grade_name}-${si}`}
            section={section}
            styles={styles}
            isDarkMode={theme.isDarkMode}
            windowWidth={windowWidth}
            onPressRow={handleBreakRowPress}
          />
        ))
      : [];

  const syllabusTechniqueChildren =
    !loading && syllabusView === 'technique'
      ? techniqueSections.map((section) => (
          <SyllabusTechniqueGradeSection
            key={`technique-${section.grade_id}`}
            section={section}
            styles={styles}
            theme={theme}
            contentWidth={techniqueContentWidth}
          />
        ))
      : [];

  const selectedLabel =
    selectedKey === 'all'
      ? 'All grades'
      : selectedKey || 'Select grade…';

  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'syllabus' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu
            navigation={navigation}
            stopwatchRef={stopwatchRef}
            currentUser={currentUser}
          />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Syllabus</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        nestedScrollEnabled
      >
        <View style={styles.syllabusIntroBlock}>
          <Text style={styles.pageTitle}>Syllabus</Text>

          <SyllabusViewToggle
            value={syllabusView}
            onChange={setSyllabusView}
            styles={styles}
            disabled={loading}
          />

          <View
            nativeID="syllabus-grade-dropdown-trigger"
            style={styles.dropdownTriggerShell}
            {...(Platform.OS === 'web'
              ? { dataSet: { syllabusGradeDropdown: 'trigger' } }
              : {})}
          >
            <TouchableOpacity
              style={styles.dropdownTriggerInner}
              onPress={() => setGradeDropdownOpen(true)}
              activeOpacity={0.7}
            >
              <Text style={styles.dropdownTriggerText}>{selectedLabel}</Text>
              <Text style={styles.dropdownChevron}>▼</Text>
            </TouchableOpacity>
          </View>

          {loading && (
            <ActivityIndicator
              size="large"
              color={theme.colors.primary}
              style={{ marginTop: 24 }}
            />
          )}
        </View>

        {syllabusScrollTableChildren}
        {syllabusTechniqueChildren}
      </ScrollView>

      <Modal
        visible={gradeDropdownOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setGradeDropdownOpen(false)}
      >
        <View style={styles.gradeModalRoot}>
          <Pressable
            style={styles.gradeModalBackdrop}
            onPress={() => {
              Keyboard.dismiss();
              setGradeDropdownOpen(false);
            }}
          />
          <View
            style={styles.dropdownSheet}
            {...(Platform.OS === 'web'
              ? { dataSet: { syllabusGradeModal: 'true' } }
              : {})}
          >
            <Text style={styles.dropdownSheetTitle}>Grade</Text>
            <ScrollView
              style={styles.dropdownScroll}
              contentContainerStyle={styles.dropdownScrollContent}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              <TouchableOpacity
                style={styles.gradePickerRow}
                onPress={() => {
                  setSelectedKey('all');
                  setGradeDropdownOpen(false);
                }}
                {...(Platform.OS === 'web'
                  ? { dataSet: { syllabusGradeRow: 'true' } }
                  : {})}
              >
                <Text style={styles.dropdownRowText}>All grades</Text>
              </TouchableOpacity>
              {grades.map((g) => (
                <TouchableOpacity
                  key={g.id}
                  style={styles.gradePickerRow}
                  onPress={() => {
                    setSelectedKey(g.grade_name);
                    setGradeDropdownOpen(false);
                  }}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { syllabusGradeRow: 'true' } }
                    : {})}
                >
                  <Text style={styles.dropdownRowText}>{g.grade_name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Video modal */}
      <Modal
        visible={videoModalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setVideoModalVisible(false)}
      >
        <View style={styles.videoModalOverlay}>
          <View style={styles.videoModalContent}>
            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setVideoModalVisible(false)}
            >
              <Text style={styles.modalCloseText}>✕</Text>
            </TouchableOpacity>
            <Text style={styles.videoModalTitle}>
              {videoBreak?.break_name || 'Video'}
            </Text>
            {videoBreak?.display_url && isYouTubeUrl(videoBreak.display_url) ? (
              Platform.OS === 'web' ? (
                <View style={styles.youtubeBox}>
                  {/* eslint-disable-next-line react/no-unknown-property */}
                  <iframe
                    title="syllabus-video"
                    src={getYouTubeEmbedUrl(videoBreak.display_url)}
                    style={{
                      border: 'none',
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#000',
                    }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </View>
              ) : (
                <WebView
                  source={{ uri: getYouTubeEmbedUrl(videoBreak.display_url) }}
                  style={styles.webView}
                  allowsFullscreenVideo
                />
              )
            ) : videoBreak?.display_url ? (
              <TouchableOpacity
                onPress={() => Linking.openURL(videoBreak.display_url)}
              >
                <Text style={styles.externalLink}>Open link</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => {
  const gold = theme.colors.primary || '#d4af37';
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 16,
      paddingHorizontal: 20,
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1 },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: { padding: 20, paddingBottom: 48 },
    pageTitle: {
      fontSize: 28,
      fontWeight: 'bold',
      color: theme.colors.heading,
      marginBottom: 16,
      textAlign: 'center',
    },
    /** Outer shell: solid surface. Inner touchable stays transparent so RN Web flex layers do not paint black. */
    dropdownTriggerShell: {
      borderRadius: 10,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#F0F0F0',
      borderWidth: theme.isDarkMode ? 1 : 0,
      borderColor: theme.isDarkMode ? '#444444' : 'transparent',
      marginBottom: 20,
      overflow: 'hidden',
    },
    dropdownTriggerInner: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 14,
      backgroundColor: 'transparent',
    },
    dropdownTriggerText: {
      fontSize: 16,
      flex: 1,
      color: theme.isDarkMode ? '#FFFFFF' : '#111111',
      fontWeight: '500',
      backgroundColor: 'transparent',
    },
    dropdownChevron: {
      fontSize: 14,
      color: theme.isDarkMode ? '#FFFFFF' : '#111111',
      backgroundColor: 'transparent',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.45)',
      justifyContent: 'center',
      padding: 20,
    },
    /** Grade picker: backdrop + sheet as siblings so ScrollView is not inside a touchable (web scroll works). */
    gradeModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    gradeModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.45)',
    },
    dropdownSheet: {
      width: '100%',
      maxWidth: 440,
      backgroundColor: theme.isDarkMode ? '#2c2c2c' : '#fff',
      borderRadius: 12,
      padding: 16,
      maxHeight: '80%',
      zIndex: 2,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }
        : { elevation: 8 }),
    },
    dropdownSheetTitle: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 10,
      color: theme.colors.text,
    },
    dropdownScroll: {
      maxHeight: 360,
      backgroundColor: 'transparent',
    },
    dropdownScrollContent: {
      paddingBottom: 8,
    },
    dropdownRow: {
      paddingVertical: 14,
      paddingHorizontal: 12,
      marginBottom: 4,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#3d3d3d' : '#f2f2f2',
      borderBottomWidth: 0,
      borderWidth: theme.isDarkMode ? 1 : 0,
      borderColor: theme.isDarkMode ? '#555555' : 'transparent',
    },
    gradePickerRow: {
      paddingVertical: 14,
      paddingHorizontal: 12,
      marginBottom: 4,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#000000' : '#f2f2f2',
      borderBottomWidth: 0,
      borderWidth: theme.isDarkMode ? 1 : 0,
      borderColor: theme.isDarkMode ? '#555555' : 'transparent',
    },
    dropdownRowText: {
      fontSize: 16,
      color: theme.isDarkMode ? '#FFFFFF' : '#111111',
      fontWeight: '500',
      backgroundColor: 'transparent',
    },
    syllabusIntroBlock: { marginBottom: 4 },
    syllabusViewToggleWrap: {
      alignItems: 'center',
      marginBottom: 16,
    },
    segmentedToggle: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: 24,
      borderWidth: 1,
      borderColor: theme.isDarkMode ? '#555555' : '#CCCCCC',
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#E8E8E8',
      padding: 3,
      minHeight: 44,
      minWidth: 220,
      alignSelf: 'center',
    },
    segmentedToggleOption: {
      flex: 1,
      minWidth: 96,
      minHeight: 38,
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    segmentedToggleOptionActiveOff: {
      backgroundColor: theme.isDarkMode ? '#757575' : '#9E9E9E',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.2,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    segmentedToggleOptionActiveOn: {
      backgroundColor: '#4CAF50',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 1 },
            shadowOpacity: 0.25,
            shadowRadius: 2,
            elevation: 2,
          }),
    },
    segmentedToggleText: {
      fontSize: 13,
      fontWeight: '700',
      letterSpacing: 0.4,
      color: theme.isDarkMode ? '#CCCCCC' : '#666666',
      backgroundColor: 'transparent',
    },
    segmentedToggleTextActive: {
      color: '#FFFFFF',
    },
    syllabusTechniqueTableCard: {
      borderRadius: 10,
      overflow: 'hidden',
      borderWidth: theme.isDarkMode ? 1 : StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
      alignSelf: 'stretch',
      width: '100%',
      backgroundColor: theme.isDarkMode ? '#1e1e1e' : '#ececec',
    },
    syllabusTechniqueHeaderRow: {
      paddingVertical: 12,
      paddingHorizontal: 18,
    },
    syllabusTechniqueHeaderCell: {
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
    },
    syllabusTechniqueDataRow: {
      alignSelf: 'stretch',
      width: '100%',
    },
    techniqueDescriptionInner: {
      paddingVertical: 18,
      paddingHorizontal: 18,
      alignSelf: 'stretch',
      width: '100%',
    },
    techniqueEmptyText: {
      fontStyle: 'italic',
      opacity: 0.72,
    },
    techniqueBulletLine: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 6,
      gap: 8,
    },
    techniqueBulletChar: {
      width: 14,
    },
    techniqueBodyText: {
      flex: 1,
    },
    section: { marginBottom: 28 },
    gradeHeaderBlock: {
      alignItems: 'center',
      marginBottom: 16,
    },
    gradeIconLarge: {
      width: 168,
      height: 168,
      alignSelf: 'center',
      borderRadius: 14,
      marginBottom: 12,
    },
    gradeTitleCentered: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.heading,
      textAlign: 'center',
    },
    syllabusHeaderRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 10,
      paddingHorizontal: 6,
    },
    syllabusHeaderRowBgDark: {
      backgroundColor: '#1e1e1e',
      borderBottomWidth: 2,
      borderBottomColor: '#FDFD0A',
    },
    syllabusHeaderRowBgLight: {
      backgroundColor: '#ececec',
      borderBottomWidth: 2,
      borderBottomColor: '#c9a227',
    },
    syllabusHeaderCell: {
      fontSize: 11,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      paddingRight: 4,
    },
    syllabusHeaderTextDark: {
      color: '#FDFD0A',
    },
    syllabusHeaderTextLight: {
      color: '#8b6914',
    },
    syllabusDataRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 11,
      paddingHorizontal: 6,
      borderWidth: 0,
    },
    syllabusCell: {
      fontSize: 13,
      paddingRight: 4,
      minWidth: 0,
    },
    syllabusVideoCol: {
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: 32,
      paddingTop: 2,
    },
    syllabusVideoHeaderIcon: { opacity: 0.95 },
    syllabusHorizontalScroll: {
      width: '100%',
      alignSelf: 'stretch',
      flexGrow: 0,
      marginHorizontal: -4,
      ...(Platform.OS === 'web' && {
        overflowX: 'auto',
      }),
    },
    syllabusHorizontalScrollContent: {
      paddingBottom: 8,
    },
    /* Matches Records list horizontal scrubber: arrows | pill track | arrows */
    hScrollBar: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
      alignSelf: 'stretch',
      paddingVertical: 10,
      paddingHorizontal: 12,
      marginTop: 6,
      borderTopWidth: 1,
      borderTopColor: theme.isDarkMode ? '#333333' : '#E0E0E0',
      borderRadius: 10,
      backgroundColor: theme.isDarkMode ? '#141414' : '#EEEEEE',
      width: '100%',
      gap: 10,
    },
    hScrollArrow: {
      width: 36,
      height: 36,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      backgroundColor: theme.isDarkMode ? '#262626' : '#E8E8E8',
    },
    hScrollArrowText: {
      fontSize: 13,
      lineHeight: 15,
      color: theme.isDarkMode ? '#FFFFFF' : '#000000',
      fontWeight: '700',
    },
    hScrollTrack: {
      flex: 1,
      flexShrink: 1,
      minWidth: 56,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#333333' : '#D0D0D0',
      position: 'relative',
      overflow: 'hidden',
    },
    hScrollThumb: {
      position: 'absolute',
      top: 0,
      height: 12,
      borderRadius: 6,
      backgroundColor: theme.isDarkMode ? '#FFFF00' : gold,
    },
    syllabusTableCard: {
      borderRadius: 10,
      overflow: 'hidden',
      borderWidth: theme.isDarkMode ? 1 : StyleSheet.hairlineWidth,
      borderColor: theme.colors.border,
    },
    /* Dark rows: both fairly dark so light text reads clearly */
    tableRowBase: {
      backgroundColor: theme.isDarkMode ? '#252525' : '#FFFFFF',
    },
    tableRowAlt: {
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
    },
    tableRowPressed: {
      opacity: 0.88,
    },
    /** RN Web: no extra layer behind glyphs (global.css forces black on some views) */
    breakRowText: {
      opacity: 1,
      backgroundColor: 'transparent',
    },
    breakTextNoUrlDark: {
      color: '#FFFFFF',
    },
    breakTextNoUrlLight: {
      color: '#0d0d0d',
    },
    emptySection: {
      fontStyle: 'italic',
      color: theme.colors.text,
      opacity: 0.7,
      paddingVertical: 8,
    },
    videoModalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.85)',
      justifyContent: 'center',
      padding: 16,
    },
    videoModalContent: {
      backgroundColor: theme.isDarkMode ? '#111' : '#222',
      borderRadius: 12,
      padding: 12,
      maxHeight: '90%',
    },
    modalCloseButton: { alignSelf: 'flex-end', padding: 8 },
    modalCloseText: { color: '#fff', fontSize: 22 },
    videoModalTitle: {
      color: '#fff',
      fontSize: 18,
      fontWeight: '700',
      marginBottom: 12,
      textAlign: 'center',
    },
    youtubeBox: { width: '100%', aspectRatio: 16 / 9, backgroundColor: '#000' },
    webView: { width: '100%', height: 220, backgroundColor: '#000' },
    externalLink: { color: '#d4af37', textAlign: 'center', padding: 20, fontSize: 16 },
    editModalBox: {
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#FFFFFF',
      borderRadius: 12,
      padding: 20,
      maxWidth: 520,
      alignSelf: 'center',
      width: '100%',
    },
    editModalTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.text,
      marginBottom: 16,
      textAlign: 'center',
    },
    toggleRow: {
      flexDirection: 'row',
      gap: 8,
      marginBottom: 12,
      flexWrap: 'wrap',
    },
    toggleChip: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
    },
    toggleChipOn: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    toggleChipText: {
      color: theme.colors.text,
      fontWeight: '600',
      fontSize: 13,
    },
    toggleChipTextOn: { color: '#000' },
    modalHint: {
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.85,
      marginBottom: 10,
    },
    modalInput: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      padding: 12,
      minHeight: 80,
      color: theme.colors.text,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FAFAFA',
      textAlignVertical: 'top',
    },
    uploadBox: { marginBottom: 8 },
    fileButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
      padding: 14,
      borderRadius: 8,
      alignItems: 'center',
      marginTop: 8,
    },
    fileButtonText: { color: '#000', fontWeight: '700' },
    successText: {
      color: '#4caf50',
      textAlign: 'center',
      marginTop: 8,
      fontWeight: '600',
    },
    editModalActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: 12,
      marginTop: 20,
    },
    modalBtn: { paddingVertical: 12, paddingHorizontal: 20, borderRadius: 8 },
    modalBtnCancel: { backgroundColor: '#666' },
    modalBtnSave: { backgroundColor: theme.colors.primary || '#d4af37' },
    modalBtnCancelText: { color: '#fff', fontWeight: '600' },
    modalBtnSaveText: { color: '#000', fontWeight: '700' },
  });
};